import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, Flex, SimpleGrid, Spinner, Input } from "@chakra-ui/react";
import { MdCategory, MdAdd, MdChevronRight } from "react-icons/md";
import { Button } from "components/ui/Button";
import FormDialog from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import {
  useDashboardActividadesMeta,
  useDashboardActividades,
} from "features/dashboard/actividades/hooks";
import { useCreateDashboardCategoria } from "features/dashboard/categorias/hooks";
import { sortCategoriasForDisplay } from "features/dashboard/categorias/sort";

const Categorias = () => {
  const navigate = useNavigate();
  const metaQ = useDashboardActividadesMeta();
  const actsQ = useDashboardActividades();
  const createCategoria = useCreateDashboardCategoria();
  const addDialog = useDialogForm({ nombre: "" });

  const categorias = useMemo(
    () => sortCategoriasForDisplay(metaQ.data?.categorias ?? []),
    [metaQ.data?.categorias]
  );
  const actividades = Array.isArray(actsQ.data) ? actsQ.data : [];

  const countsPorCategoria = useMemo(() => {
    const counts = {};
    actividades.forEach((a) => {
      (a.categoriaIds ?? []).forEach((id) => {
        counts[id] = (counts[id] ?? 0) + 1;
      });
    });
    return counts;
  }, [actividades]);

  const onAdd = async () => {
    const nombre = addDialog.form.nombre.trim();
    if (!nombre) return;
    try {
      await createCategoria.mutateAsync(nombre);
      addDialog.close();
      addDialog.reset();
    } catch (_e) {}
  };

  return (
    <Box>
      {/* Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
        mb="6"
      >
        <Box>
          <Text
            fontFamily="display"
            fontWeight="700"
            fontSize={{ base: "2xl", md: "3xl" }}
            color="dashboard.fg.default"
            lineHeight="1.1"
          >
            Categorías
          </Text>
          <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
            Organiza tus actividades por categoría
          </Text>
        </Box>

        <Button
          color="red"
          variant="solid"
          onClick={() => addDialog.openWith()}
          radius="xl"
          fontSize="lg"
          alignSelf={{ base: "stretch", md: "auto" }}
        >
          <MdAdd />
          Nueva categoría
        </Button>
      </Flex>

      {/* Loading */}
      {metaQ.isLoading && (
        <Flex justify="center" align="center" py="12">
          <Spinner />
        </Flex>
      )}

      {/* Error */}
      {metaQ.isError && (
        <pre style={{ color: "crimson", whiteSpace: "pre-wrap" }}>
          {String(metaQ.error?.message || metaQ.error)}
        </pre>
      )}

      {/* Empty state */}
      {!metaQ.isLoading && categorias.length === 0 && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="10"
          textAlign="center"
        >
          <Text color="dashboard.fg.muted">
            No hay categorías. Crea una con <strong>Nueva categoría</strong>.
          </Text>
        </Box>
      )}

      {/* Grid */}
      {!metaQ.isLoading && categorias.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap="4">
          {categorias.map((cat) => {
            const count = countsPorCategoria[cat.id] ?? 0;
            return (
              <Box
                key={cat.id}
                bg="dashboard.bg.surface"
                borderWidth="1px"
                borderColor="dashboard.border.muted"
                rounded="card"
                p="5"
                cursor="pointer"
                transition="transform 150ms ease, box-shadow 150ms ease"
                _hover={{ transform: "translateY(-2px)", boxShadow: "cardLift" }}
                onClick={() => navigate(`/dashboard/club/categorias/${cat.id}`)}
              >
                <Flex align="center" gap="4">
                  <Box
                    w="48px"
                    h="48px"
                    borderRadius="14px"
                    bg="purple.500"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <MdCategory size="24px" color="white" />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text
                      fontFamily="display"
                      fontWeight="700"
                      fontSize="md"
                      color="dashboard.fg.default"
                      lineClamp={1}
                    >
                      {cat.nombre}
                    </Text>
                    <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
                      {count} {count === 1 ? "actividad" : "actividades"}
                    </Text>
                  </Box>
                  <MdChevronRight
                    size="20px"
                    style={{ color: "var(--chakra-colors-dashboard-fg-subtle)", flexShrink: 0 }}
                  />
                </Flex>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* Add dialog */}
      <FormDialog
        open={addDialog.open}
        onOpenChange={addDialog.onOpenChange}
        title="Nueva categoría"
        saving={createCategoria.isPending}
        onCancel={addDialog.close}
        onSave={onAdd}
        saveLabel="Crear"
        errorText={
          createCategoria.isError
            ? String(createCategoria.error?.message || createCategoria.error)
            : ""
        }
      >
        <Input
          placeholder="Nombre de la categoría"
          bg="dashboard.bg.input"
          borderColor="dashboard.border.default"
          {...addDialog.bind("nombre")}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
        />
      </FormDialog>
    </Box>
  );
};

export default Categorias;
